import React, { useMemo } from "react";

// import { IRRRow } from "../Sub-table/IRRRow";
import {
  Autocomplete,
  CircularProgress,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Toolbar,
  Typography
} from "@mui/material";
import { DealTableRow, GroupByContent, GroupByMenu } from "components/reports";
import { DEAL_INDEX, indexNames } from "constants/reports";
import { getShownCalculatedFields, useReportsActions, useReportsQueriesSelector } from "store";
import { ReportTableData } from "store/types";

import { FiltersView } from "../Filters-view/FiltersVIew";
import { GroupByMenuSettings } from "../Group-by-content/GroupByContent";

import "./RisksTable.styles.scss";

interface RisksTableProps extends GroupByMenuSettings {
  isLoading: boolean;
  data?: ReportTableData;
  widthTitle?: boolean;
  selectedIndex?: string;
  setSelectedIndex: (value: string | undefined) => void;
}

export const RisksTable: React.FC<RisksTableProps> = React.memo(
  ({
    showGroupBy = true,
    showRowBy = true,
    isLoading,
    data,
    widthTitle = false,
    showRiskMetrics = true,
    showCharacteristics = false,
    selectedIndex,
    setSelectedIndex
  }) => {
    const { filteredPortfolio } = useReportsQueriesSelector();

    const indexOptions = [
      {
        key: DEAL_INDEX,
        value: "Use deal specific index"
      },
      ...Object.entries(indexNames).map(([key, value]) => ({
        key,
        value
      }))
    ];

    const { setQueriesAction } = useReportsActions();
    const calculatedFields = getShownCalculatedFields();
    const table = useMemo(() => {
      if (!data) {
        return;
      }
      return (
        <Table>
          <TableHead>
            <TableRow>
              {widthTitle && (
                <TableCell key="empty" sx={{ position: "sticky", left: 0, zIndex: 10, background: "white" }} />
              )}
              {data?.columns.map((col) => (
                <TableCell key={col.key} align="left">
                  {col.key === "calcField-pme" ? (
                    <Stack direction="row" alignItems="center" gap="10px">
                      <Typography fontSize={13} fontWeight={500}>
                        {col.title}
                      </Typography>
                      <Autocomplete
                        options={indexOptions}
                        value={indexOptions.find((option) => option.key === (selectedIndex || DEAL_INDEX))}
                        disableClearable
                        filterOptions={(options) => options}
                        onChange={(_, option) => {
                          setSelectedIndex(option.key === DEAL_INDEX ? undefined : option.key);
                        }}
                        renderInput={(params) => <TextField {...params} size="small" />}
                        getOptionLabel={(option) => option.value}
                        sx={{
                          minWidth: "178px",
                          "& .MuiOutlinedInput-root": {
                            padding: "2px 39px 2px 2px !important"
                          }
                        }}
                      />
                    </Stack>
                  ) : (
                    <Typography fontSize={13} fontWeight={500}>
                      {col.title}
                    </Typography>
                  )}
                </TableCell>
              ))}
              {/* {Object.entries(calculatedFields).map(([field, meta]) => {
              return (
                <TableCell align="center" key={field}>
                  <span>{meta.title}</span>
                </TableCell>
              );
            })} */}
            </TableRow>
          </TableHead>

          <TableBody>
            {data?.rows.map((row, index) => (
              <DealTableRow key={row.id || index} row={row} columns={data.columns} isRowTransparent />
              // <IRRRow
              //   key={row.id || index}
              //   row={row}
              //   calculatedFields={calculatedFields}
              //   cellProps={{ align: "center" }}
              //   withTitle={widthTitle}
              // />
            ))}
          </TableBody>
        </Table>
      );
    }, [widthTitle, data, calculatedFields, selectedIndex, setSelectedIndex, indexOptions]);

    return (
      <TableContainer className="risks-table">
        <Toolbar sx={{ "&": { padding: "0" } }} className="risks-table__toolbar">
          <div className="risks-table__toolbar-tune">
            <span className="title">Overview</span>

            <div className="risks-table__toolbar-tune-configuration">
              <div className="risks-table__toolbar-tune-configuration-toggle">
                {/* <span className="text">Filtered Portfolio</span> */}
                <span className="text">Show My Portfolio</span>
                <Switch
                  sx={{
                    color: "#173c4c",
                    "& .MuiSwitch-switchBase.Mui-checked": {
                      color: "#26a6ae"
                    },
                    "& .MuiSwitch-track": {
                      backgroundColor: "#f2f2f2"
                    }
                  }}
                  checked={filteredPortfolio}
                  onChange={(e) => setQueriesAction({ filteredPortfolio: e.target.checked })}
                />
              </div>
              <GroupByMenu>
                <GroupByContent
                  showGroupBy={showGroupBy}
                  showRowBy={showRowBy}
                  showCharacteristics={showCharacteristics}
                  showRiskMetrics={showRiskMetrics}
                />
              </GroupByMenu>
            </div>
          </div>
          {filteredPortfolio && (
            <div className="risks-table__toolbar-filters">
              <FiltersView title="Filter my portfolio" limitTags={3} />
            </div>
          )}
        </Toolbar>
        {isLoading ? (
          <div className="risks-table-loading">
            <CircularProgress size={100} />
          </div>
        ) : (
          <div className="risks-table__scroll">{table}</div>
        )}
      </TableContainer>
    );
  }
);
